<template>
    <div>
        <Head>
            <title>{{ meta.title }} | Rothco</title>
            <meta name="description" :content="meta.meta_desc"/>
            <meta name="keywords" :content="meta.meta_keywords"/>
            <link v-if="isCollection" rel="canonical" :href="route('productListing.collection', { collection: searchTerm })"/>
        </Head>
        <div v-if="searchSpringResults.results.length" class="plp">
            
            <h2 v-if="isCollection" class="headline-xl results-header">
                Shop The Collection
            </h2>
            <h2 v-else-if="isNew" class="headline-xl results-header">
                New Arrivals
            </h2>
            <h2 v-else-if="isBestSellers" class="headline-xl results-header">
                Best Sellers
            </h2>
            <h2 v-else class="headline-xl results-header">
                Results for "{{ searchTerm }}"
            </h2>
            <ProductTiles
                id="product-tiles"
                data-allow-mismatch
                :searchSpringData="searchSpringResults"
                :handleSort="handleSort"
                :handlePagination="handlePagination"
                v-model:sort-option="defaultSort"
                :paginationKey="key"
                :handle-filter="handleFilter"
                :searchTerm="isCollection ? '' : searchTerm"
            />
        </div>
        <div v-else-if="initialLoad" class="plp">
            <h2 class="headline-xl results-header text-center">
                Loading...
            </h2>
        </div>
        <div v-else class="plp no-search-results">
            <Cms ref="cmsCategories" model="categories" driver="builder" is-data v-show="false" v-model="categoryData" :searchParams="categorySearchParams"/>

            <h2 id="no-results-text" class="headline-xl results-header text-center">
                {{ noResultsText }}
            </h2>

            <div
                v-if="
                    trendingSearches.trending &&
                    trendingSearches.trending.queries.length
                "
                class="trending-searches"
            >
                <div class="description">
                    <p>Try another search below or explore some of our popular search terms and best selling gear</p>
                </div>

                <p class="headline-s text-center heading">Popular Search Terms:</p>

                <div class="search-terms-wrapper">
                        <InertiaLink
                            v-for="(term, index) in firstFiveTrending"
                            :key="index"
                            class="secondary grey term-btn"
                            as="button"
                            :href="`/search/keyword/${term.searchQuery}`"
                            >{{ term.searchQuery }}</InertiaLink>
                </div>
            </div>
            <div class="categories" v-if="userType==='b2b'">
                <SubCategoryLaunchers text-below columns="three" :togglePadding="true"/>
            </div>

            <div v-if="!isBestSellers" class="best-sller-carousel">
                <product-carousel :heading="bestSellerHeading" ref="productCarouselRef" :togglePadding="true" :isBestSellers="true" allowQuickView/>
            </div>

            <div class="help">
                <FeaturedContent titleColor="rothcoBlack" :isSlider="false" :mediaCards="filteredMediaCards" :hasPadding="false"/>
            </div>

            <Cms
                ref="cms"
                model="product-listing"
                :url="`/index/search/keyword/${searchTerm}`"
                driver="builder"
                :components="getBuilderComponents()"
                is-page
            />
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { ref, toRefs, computed, watch, inject, onMounted } from "vue";
    import { getBuilderComponents } from "@root/builder-components.ts";
    import SearchSpringApi from "@/helpers/api/SearchSpringAPI.ts";
    import {
        useSearchByTerm,
        useTrending
    } from "@/helpers/composables/searchSpringApi";
    import ProductTiles from "@/components/ProductTiles.vue";
    import SubCategoryLaunchers from "@/components/SubCategoryLaunchers.vue";
    import ProductCarousel from "@/components/ProductCarousel.vue";
    import FeaturedContent from "@/components/FeaturedContent.vue";
    import contactUsImage from "@/assets/images/contact-us.jpg";
    import becomeDealerImage from "@/assets/images/become-a-dealer.jpg";
    import { useSettingsStore } from "@/store/settings";
    import Cms from "@/components/Cms.vue";
    import { Link as InertiaLink, usePage, Head } from "@inertiajs/vue3";
    import { useCategoryLandingStore } from '@/store/categoryLandingStore';
    import { storeToRefs } from "pinia";
    import useGoogleManager from "@/helpers/composables/google";
    let route = inject('route');
    let { gtmSearchResultsEvent } = useGoogleManager();
    const cms = ref(null);
    let props = defineProps({
        isCollection: {
            type: Boolean,
            required: false,
            default: false
        },
        searchTerm: {
            type: String,
            required: true
        },
        isNew: {
            type: Boolean,
            required: false,
            default: false
        },
        isBestSellers: {
            type: Boolean,
            required: false,
            default: false
        },
        meta: {
            type: Object,
            required: false,
            default: (rawProps) => {
                if (rawProps.isNew) {
                    return {
                        title: "New Arrivals",
                        meta_desc: "Discover the latest new arrivals at Rothco.",
                        meta_keywords: "Rothco, New Arrivals, Latest Products"
                    };
                } else if (rawProps.isBestSellers) {
                    return {
                        title: "Best Sellers",
                        meta_desc: "Shop the best selling products at Rothco.",
                        meta_keywords: "Rothco, Best Sellers, Popular Products"
                    };
                } else {
                    return {
                        title: `Search Results for ${rawProps.searchTerm}`,
                        meta_desc: "Search results for Rothco products",
                        meta_keywords: "Rothco, Search, Products"
                    };
                }
            }
        }
    });
    let { searchTerm } = toRefs(props);

    let settingsStore = useSettingsStore();

    const userType = computed(() => settingsStore.userType);

    const bestSellerHeading = ref({
        text: 'Best Sellers'
    });

    const mediaCards = ref([
        {
            showFor: 'all',
            title: "Need Help? Contact Us",
            description: "If you need support with an order, product, or have any questions, our US-based team of customer care exper is ready to help!",
            mediaProps: {
                defaultMediaType: "image",
                media: contactUsImage
            },
            cta: {
                type: "link",
                label: "Customer Service",
                url: {
                    generalUrl: {value: {data: {alias: "customer-service.index"}}},
                    urlType: "general"
                },
            }
        },
        {
            showFor: 'b2c',
            title: "Become a Dealer",
            description: "Sign up to receive exclusive pricing and premier service for your business.",
            mediaProps: {
                defaultMediaType: "image",
                media: becomeDealerImage
            },
            cta: {
                type: "link",
                label: "Apply Today",
                url: {
                    generalUrl: {value: {data: {alias: "index.becomeADealer"}}},
                    urlType: "general"
                },
            }
        },
        {
            showFor: 'b2b',
            title: "Dealer FAQ",
            description: "Find answers to the most common questions about orders, shipping, dealer support, and how to contact us.",
            mediaProps: {
                defaultMediaType: "image",
                media: becomeDealerImage
            },
            cta: {
                type: "link",
                label: "Check FAQ",
                url: {
                    generalUrl: {value: {data: {alias: "customer-service.faqs"}}},
                    urlType: "general"
                }
            }
        }
     ]);

     const filteredMediaCards = computed(() => {
        return mediaCards.value.filter(card => card.showFor === userType.value || card.showFor === 'all');
    });
    let {
        searchSpringResults,
        handleSort,
        handlePagination,
        defaultSort,
        key,
        handleFilter,
        initialLoad
    } = useSearchByTerm({
        searchSpringApi: new SearchSpringApi(),
        [props.isCollection ? 'tag': 'searchTerm']: props.isNew || props.isBestSellers? undefined : searchTerm.value,
        filters: props.isNew ? [
            {
                field: 'is_new',
                value: 'true',
                multiple: 'single'
            }
        ] : []
    });
    let { trendingSearches } = useTrending({ searchSpringApi: new SearchSpringApi() });
    let { setBuilderCategories, setChildCategories } = useCategoryLandingStore();

    let firstFiveTrending = computed(() =>
        trendingSearches.value?.trending?.queries.slice(0, 5)
    );

    let categoryData = ref([]);
    let {categories} = storeToRefs(useSettingsStore());
    let clothingCategory = computed(() => {
        return categories.value.find((cat) => cat.seo_name === 'rothco-clothing') ?? {
            child_category: []
        };
    });
    let categorySearchParams = computed(() => {
        return {
            "data.name.$in": clothingCategory.value?.child_category?.map((cat) => cat.seo_name) ?? []
        };
    });

    setChildCategories(clothingCategory.value.child_category);

    watch(() => categoryData, (newVal) => {
        if (newVal.value.length > 0) {
            setBuilderCategories(newVal.value);
        }
    }, { deep: true });

    let noResultsText = computed(() => {
        if (props.isCollection) {
            return "No results found for this collection";
        } else if (props.isNew) {
            return "No new arrivals found";
        } else if (props.isBestSellers) {
            return "No best sellers found";
        } else {
            return `No results found for "${searchTerm.value}"`;
        }
    });

    onMounted(()=>{
        if(props.searchTerm){
            gtmSearchResultsEvent(props.searchTerm);
        }
    })
</script>
